import React, { useState, useCallback, useRef } from 'react';
import { FaSearch, FaChevronDown, FaTimes } from 'react-icons/fa';

const RAGA_API = 'https://om-studio-api.codeandtech.com';

// ─── Swara → semitone offset from Sa ─────────────────────────────────────────
const OFFSETS = {
  S: 0, R1: 1, R2: 2, G1: 2, R3: 3, G2: 3, G3: 4,
  M1: 5, M2: 6, P: 7,
  D1: 8, D2: 9, N1: 9, D3: 10, N2: 10, N3: 11, "S'": 12,
};

const BLACK_PITCHES = new Set([1, 3, 6, 8, 10]);

const SHRUTIS = [
  { label: 'C  — 1', shift: 0 },
  { label: 'C♯ — 1½', shift: 1 },
  { label: 'D  — 2', shift: 2 },
  { label: 'D♯ — 2½', shift: 3 },
  { label: 'E  — 3', shift: 4 },
  { label: 'F  — 3½', shift: 5 },
  { label: 'F♯ — 4', shift: 6 },
  { label: 'G  — 5', shift: 7 },
  { label: 'G♯ — 5½', shift: 8 },
  { label: 'A  — 6', shift: 9 },
  { label: 'A♯ — 6½', shift: 10 },
  { label: 'B  — 7', shift: 11 },
];

const TONIC_NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];

// ─── RagaKeyboard ─────────────────────────────────────────────────────────────
// Props:
//   label     — "Arohanam" | "Avarohanam"
//   notes     — raga.arohanam or raga.avarohanam (string[])
//   baseShift — shruti semitone shift, 0 = C
function RagaKeyboard({ label, notes, baseShift = 0 }) {
  const { selectedPitches, pitchLabels } = React.useMemo(() => {
    const sel = new Set();
    const lab = {};
    for (const note of (notes || [])) {
      const offset = OFFSETS[note];
      if (offset == null) continue;
      const pitch = (baseShift + offset) % 12;
      sel.add(pitch);
      (lab[pitch] = lab[pitch] || []).push(note);
    }
    return { selectedPitches: sel, pitchLabels: lab };
  }, [notes, baseShift]);

  if (!notes?.length) return null;

  // 14-key window starting 1 semitone before Sa (matches OM Studio mobile)
  const startPitch = (baseShift - 1 + 12) % 12;
  const pitches = Array.from({ length: 14 }, (_, i) => (startPitch + i) % 12);
  const tonicName = TONIC_NAMES[baseShift % 12];

  return (
    <figure style={{ margin: '0.75rem 0 0' }}>
      <figcaption style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: '0.8px',
        color: 'var(--brown-mid)',
        textTransform: 'uppercase',
        marginBottom: '0.4rem',
      }}>
        {label}
      </figcaption>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        padding: '1.25rem 0.75rem 0.75rem',
        gap: 0,
        background: '#281543',
        borderRadius: '0.5rem',
        overflowX: 'auto',
      }}>
        {pitches.map((pitch, i) => {
          const isBlack = BLACK_PITCHES.has(pitch);
          const isSel = selectedPitches.has(pitch);
          const labels = pitchLabels[pitch] || [];
          return (
            <div key={i} style={{
              position: 'relative',
              flex: `0 0 ${isBlack ? '1.8rem' : '3.4rem'}`,
              height: isBlack ? '3.8rem' : '6rem',
              margin: isBlack ? '0 -.9rem' : 0,
              zIndex: isBlack ? 1 : 0,
              alignSelf: isBlack ? 'flex-start' : 'stretch',
              background: isBlack
                ? (isSel ? '#824f14' : '#2a193f')
                : (isSel ? '#fffdf8' : '#fffdf8'),
              border: '1px solid #372b43',
              borderRadius: isBlack ? '0 0 .25rem .25rem' : 0,
              boxShadow: (!isBlack && isSel) ? 'inset 0 -.45rem #e69b21' : 'none',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              paddingBottom: '.4rem',
              color: isBlack ? '#f8df9d' : '#1a0a3e',
              fontSize: '.72rem',
              fontWeight: 700,
            }}>
              {labels.map(n => (
                <span key={n} style={{ textAlign: 'center' }}>{n}</span>
              ))}
            </div>
          );
        })}
      </div>
      <p style={{
        fontSize: '0.72rem',
        color: 'var(--text-muted)',
        margin: '0.3rem 0 0',
        fontStyle: 'italic',
      }}>
        Sa at {tonicName} — scale positions only, not gamaka
      </p>
    </figure>
  );
}

// ─── RagaLookup (search bar inline widget) ───────────────────────────────────
// Props:
//   onRagaSelect — callback(raga | null) called when a raga is picked or cleared
export function RagaSearchBar({ onRagaSelect, inputWidth = 220 }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);

  const search = useCallback((q) => {
    clearTimeout(timer.current);
    setError('');
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${RAGA_API}/v1/ragas/search?q=${encodeURIComponent(q.trim())}`
        );
        if (!res.ok) throw new Error(`API error ${res.status}`);
        const { items } = await res.json();
        setResults(items || []);
      } catch {
        setError('Could not search ragas');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  }, []);

  function select(raga) {
    setQuery(raga.name);
    setResults([]);
    onRagaSelect(raga);
  }

  function clear() {
    setQuery('');
    setResults([]);
    onRagaSelect(null);
  }

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px' }}>
      {/* Placeholder colour for the sky-blue input */}
      <style>{`#np-raga-search::placeholder { color: #5ba3c9; opacity: 1; }`}</style>
      {/* Textbox */}
      <div style={{ position: 'relative' }}>
        <input
          id="np-raga-search"
          aria-label="Search raga"
          value={query}
          onChange={e => { setQuery(e.target.value); search(e.target.value); if (!e.target.value) clear(); }}
          placeholder="Search raga name…"
          style={{
            width: inputWidth,
            padding: '0.42rem 0.7rem',
            paddingRight: query ? '3.2rem' : '1.8rem',
            border: '1.5px solid var(--gold-mid)',
            borderRadius: 'var(--radius-pill)',
            background: '#e0f4ff',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-heading)',
            fontSize: '0.78rem',
            outline: 'none',
            transition: 'border-color 0.18s, box-shadow 0.18s',
          }}
          onFocus={e => { e.target.style.borderColor = '#0ea5e9'; e.target.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.2)'; }}
          onBlur={e => { e.target.style.borderColor = 'var(--gold-mid)'; e.target.style.boxShadow = 'none'; }}
        />

        {/* Right-side icons: spinner | clear + search */}
        <span style={{
          position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', gap: '0.3rem',
        }}>
          {loading ? (
            <span style={{
              display: 'inline-block', width: 12, height: 12,
              border: '2px solid var(--gold-light)', borderTop: '2px solid var(--gold-deep)',
              borderRadius: '50%', animation: 'np-spin 0.9s linear infinite',
            }} />
          ) : (
            <>
              {query && (
                <FaTimes
                  role="button"
                  aria-label="Clear raga search"
                  onMouseDown={e => { e.preventDefault(); clear(); }}
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--maroon)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                />
              )}
              <FaSearch style={{ color: 'var(--gold-deep)', fontSize: '0.7rem', pointerEvents: 'none' }} />
            </>
          )}
        </span>

        {/* Dropdown results */}
        {results.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            width: 260,
            zIndex: 2000,
            background: 'var(--parchment)',
            border: '1px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-deep)',
            overflow: 'hidden',
          }}>
            {results.map(r => (
              <button
                key={r.id}
                type="button"
                onMouseDown={() => select(r)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  borderBottom: '1px solid var(--border-light)',
                  background: 'transparent',
                  padding: '0.6rem 0.9rem',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.85rem',
                  color: 'var(--text-primary)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-glow)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <strong style={{ fontFamily: 'var(--font-heading)', fontSize: '0.82rem' }}>{r.name}</strong>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  {r.kind === 'melakarta' ? `M ${r.melakartaNumber}` : 'Janya'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error badge */}
      {error && (
        <span style={{ fontSize: '0.7rem', color: 'var(--maroon)', fontStyle: 'italic' }}>{error}</span>
      )}
    </div>
  );
}

// ─── RagaAccordion — placed below song content ────────────────────────────────
// Props:
//   raga — raga object from API (or null)
export function RagaAccordion({ raga }) {
  const [open, setOpen] = useState(true);   // auto-open when raga arrives
  const [shrutiShift, setShrutiShift] = useState(0);

  // Auto-open whenever a new raga is selected
  React.useEffect(() => { if (raga) setOpen(true); }, [raga]);

  if (!raga) return null;

  return (
    <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
      <hr className="np-divider-thin" />

      {/* Accordion header */}
      <div
        className="np-accordion-header np-panel"
        style={{ padding: '0.65rem 1rem', marginBottom: 0, borderRadius: open ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)' }}
        onClick={() => setOpen(v => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setOpen(v => !v); }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1rem' }}>🎵</span>
          <span style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: 'var(--maroon-mid)',
            letterSpacing: '0.5px',
          }}>
            {raga.name}
          </span>
          <span style={{
            background: 'linear-gradient(135deg, var(--maroon), var(--brown-mid))',
            color: 'var(--gold-light)',
            fontFamily: 'var(--font-heading)',
            fontSize: '0.65rem',
            fontWeight: 700,
            padding: '1px 8px',
            borderRadius: 'var(--radius-pill)',
            letterSpacing: '0.5px',
          }}>
            {raga.kind === 'melakarta' ? `Melakarta ${raga.melakartaNumber}` : 'Janya Raga'}
          </span>
        </div>
        <FaChevronDown
          className={`np-accordion-chevron${open ? ' open' : ''}`}
          aria-hidden="true"
        />
      </div>

      {/* Accordion body */}
      {open && (
        <div className="np-panel" style={{
          borderTop: 'none',
          borderRadius: '0 0 var(--radius-md) var(--radius-md)',
          padding: '1rem 1.25rem 1.25rem',
        }}>
          {/* Scale text + shruti selector in one row */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {/* Scales */}
            <div style={{ flex: 1, minWidth: 180 }}>
              <p style={{ margin: '0 0 0.35rem', fontFamily: 'var(--font-body)', fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                <strong style={{ fontFamily: 'var(--font-heading)', color: 'var(--brown-mid)', letterSpacing: '0.5px' }}>Arohanam:</strong>
                {'  '}
                <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, letterSpacing: '1px' }}>
                  {raga.arohanam.join(' · ')}
                </span>
              </p>
              <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                <strong style={{ fontFamily: 'var(--font-heading)', color: 'var(--brown-mid)', letterSpacing: '0.5px' }}>Avarohanam:</strong>
                {'  '}
                <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 600, letterSpacing: '1px' }}>
                  {raga.avarohanam.join(' · ')}
                </span>
              </p>
            </div>

            {/* Shruti selector */}
            <div>
              <label htmlFor="np-shruti" style={{
                display: 'block',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.8px',
                color: 'var(--brown-mid)',
                textTransform: 'uppercase',
                marginBottom: '0.3rem',
              }}>
                Shruti
              </label>
              <select
                id="np-shruti"
                value={shrutiShift}
                onChange={e => setShrutiShift(Number(e.target.value))}
                style={{
                  padding: '0.4rem 0.7rem',
                  border: '1.5px solid var(--gold-mid)',
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  background: 'var(--parchment)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  minWidth: '7rem',
                  outline: 'none',
                }}
              >
                {SHRUTIS.map(s => (
                  <option key={s.shift} value={s.shift}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Keyboard diagrams */}
          <RagaKeyboard label="Arohanam" notes={raga.arohanam} baseShift={shrutiShift} />
          <RagaKeyboard label="Avarohanam" notes={raga.avarohanam} baseShift={shrutiShift} />

          {/* Anya-swaras note */}
          {raga.anyaSwaras && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.75rem', fontStyle: 'italic' }}>
              Note: {raga.anyaSwaras}
            </p>
          )}

          {/* Aliases */}
          {raga.aliases?.length > 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
              Also known as: {raga.aliases.join(', ')}
            </p>
          )}

          {/* Attribution */}
          {raga.source && (
            <p style={{
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              marginTop: '1rem',
              paddingTop: '0.6rem',
              borderTop: '1px solid var(--border-light)',
            }}>
              Source:{' '}
              <a
                href={raga.source.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text-link)' }}
              >
                Wikipedia — {raga.source.page}
              </a>{' '}
              ({raga.source.license})
            </p>
          )}
        </div>
      )}
    </div>
  );
}
